import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Send,
  MapPin,
  ExternalLink,
  Shield,
  AlertTriangle,
  Clock,
  Calendar,
  CheckCheck,
  Lock,
  User,
  Phone,
  Sparkles
} from 'lucide-react';
import { BookingRecord, ChatMessageRecord } from '../../types';
import { fetchBookingChatMessages, sendBookingChatMessage, disputeV2Booking } from '../../lib/bookingService';
import { realtimeSocket } from '../../lib/socket';

interface BookingChatModalProps {
  booking: BookingRecord;
  currentUserRole: 'client' | 'companion';
  currentUserId: string;
  currentUserName: string;
  onClose: () => void;
  onStatusUpdated?: () => void;
}

export const BookingChatModal: React.FC<BookingChatModalProps> = ({
  booking,
  currentUserRole,
  currentUserId,
  currentUserName,
  onClose,
  onStatusUpdated
}) => {
  const [messages, setMessages] = useState<ChatMessageRecord[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isChatLocked, setIsChatLocked] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [isDisputing, setIsDisputing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const otherPersonName =
    currentUserRole === 'client' ? booking.companion_name : booking.client_name;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const res = await fetchBookingChatMessages(booking.id);
      if (res.success) {
        setMessages(res.messages);
        setIsChatLocked(!res.isChatEnabled);
      } else {
        setIsChatLocked(true);
        setLockReason(res.error || 'Chat is locked until booking is accepted.');
      }
    } catch {
      // Safe fallback
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();

    // Subscribe to realtime chat messages over WebSocket
    const unsub = realtimeSocket.subscribe('CHAT_MESSAGE', (payload: any) => {
      if (payload && payload.bookingId === booking.id && payload.message) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === payload.message.id)) return prev;
          return [...prev, payload.message];
        });
      }
    });

    return () => {
      unsub();
    };
  }, [booking.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isSending || isChatLocked) return;

    const textToSend = inputText.trim();
    setInputText('');
    setIsSending(true);

    try {
      const res = await sendBookingChatMessage(
        booking.id,
        currentUserId,
        currentUserName,
        currentUserRole,
        textToSend
      );

      if (res.success && res.messageRecord) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === res.messageRecord!.id)) return prev;
          return [...prev, res.messageRecord!];
        });
      }
    } catch (err) {
      console.error('Failed to send chat message:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleDisputeSubmit = async () => {
    if (!disputeReason.trim()) return;
    setIsDisputing(true);
    try {
      await disputeV2Booking(booking.id, currentUserRole, disputeReason.trim());
      setShowDisputeModal(false);
      if (onStatusUpdated) onStatusUpdated();
    } catch {
      // Safe
    } finally {
      setIsDisputing(false);
    }
  };

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${booking.latitude || 22.5726},${booking.longitude || 88.3639}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
      <div className="relative w-full max-w-2xl bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl flex flex-col h-[85vh] max-h-[720px] overflow-hidden">
        {/* Header Bar */}
        <div className="p-4 sm:p-5 bg-slate-950 border-b border-slate-800 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                {otherPersonName.charAt(0)}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-slate-950" />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white text-sm sm:text-base leading-tight">
                  {otherPersonName}
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-purple-950 border border-purple-700 text-purple-300 text-[10px] font-mono font-bold">
                  #{booking.id}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                {currentUserRole === 'client' ? 'Companion Host' : 'Client'} • Encrypted Private Room
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDisputeModal(true)}
              className="p-2 text-slate-400 hover:text-rose-400 bg-slate-900 hover:bg-slate-850 rounded-xl border border-slate-800 text-xs transition-colors cursor-pointer"
              title="Report or Dispute Booking"
            >
              <AlertTriangle className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white bg-slate-900 hover:bg-slate-850 rounded-xl border border-slate-800 text-xs transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Meeting Information Ribbon (One-click Google Maps) */}
        <div className="px-4 py-2.5 bg-slate-950/80 border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-2 text-xs shrink-0">
          <div className="flex items-center gap-3 text-slate-300">
            <div className="flex items-center gap-1 font-mono text-[11px] text-purple-300">
              <Calendar className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              <span>{booking.booking_date}</span>
            </div>
            <div className="flex items-center gap-1 font-mono text-[11px] text-purple-300">
              <Clock className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              <span>{booking.start_time} ({booking.duration_hours}h)</span>
            </div>
            <div className="flex items-center gap-1 text-slate-300 text-[11px] truncate max-w-[200px] sm:max-w-xs">
              <MapPin className="w-3.5 h-3.5 text-rose-400 shrink-0" />
              <span className="truncate">{booking.location_name}</span>
            </div>
          </div>

          {/* One-Click Google Maps Link (Rule 10) */}
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-700 text-emerald-300 text-[11px] font-bold transition-all shadow-sm shrink-0"
          >
            <MapPin className="w-3 h-3 text-emerald-400" />
            <span>Open in Google Maps</span>
            <ExternalLink className="w-2.5 h-2.5 opacity-70" />
          </a>
        </div>

        {/* Chat Locked Banner (Rule 8: Chat Access Rule) */}
        {isChatLocked ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-amber-950/80 border border-amber-700/80 text-amber-400 flex items-center justify-center">
              <Lock className="w-8 h-8" />
            </div>
            <div className="max-w-md space-y-1">
              <h4 className="text-base font-bold text-white">Private Chat Locked</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                {lockReason || 'Direct chat is unlocked exclusively after payment is verified and your companion formally accepts the booking.'}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-950 border border-slate-800 text-[11px] text-purple-300 font-mono">
              <Shield className="w-3.5 h-3.5 text-purple-400" />
              <span>PairUpToday Anti-Circumvention Escrow Protection</span>
            </div>
          </div>
        ) : (
          <>
            {/* Messages Scroll Area */}
            <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-slate-900/60">
              {/* Meeting Safety Notice Card */}
              <div className="p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 text-xs space-y-1 text-slate-400">
                <div className="flex items-center gap-1.5 text-purple-400 font-bold">
                  <Shield className="w-3.5 h-3.5" />
                  <span>Platform Safety Notice</span>
                </div>
                <p className="text-[11px] text-slate-400 leading-snug">
                  Please keep all communication regarding your meeting details, timing, and public location inside this chat.
                  Always meet in public places. When the meetup completes, the client will share the 6-digit completion OTP with the companion.
                </p>
              </div>

              {isLoading ? (
                <div className="py-8 text-center text-xs text-slate-500 font-mono">
                  Loading encrypted messages...
                </div>
              ) : messages.length === 0 ? (
                <div className="py-12 text-center space-y-2">
                  <p className="text-sm font-bold text-slate-300">No messages yet</p>
                  <p className="text-xs text-slate-500">
                    Say hello to {otherPersonName} and coordinate your meeting!
                  </p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMe = msg.sender_role === currentUserRole;
                  return (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} space-y-1`}
                    >
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono px-1">
                        <span>{msg.sender_name}</span>
                        <span>•</span>
                        <span>{msg.timestamp}</span>
                      </div>
                      <div
                        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed break-words ${
                          isMe
                            ? 'bg-purple-600 text-white rounded-br-sm shadow-md shadow-purple-950/40'
                            : 'bg-slate-800 text-slate-100 rounded-bl-sm border border-slate-700/80'
                        }`}
                      >
                        {msg.message}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <form
              onSubmit={handleSendMessage}
              className="p-3 sm:p-4 bg-slate-950 border-t border-slate-800 flex items-center gap-2 shrink-0"
            >
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={`Message ${otherPersonName}...`}
                className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-600 transition-colors"
              />
              <button
                type="submit"
                disabled={!inputText.trim() || isSending}
                className="p-2.5 rounded-2xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:hover:bg-purple-600 text-white transition-all cursor-pointer shadow-md shadow-purple-950"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </>
        )}

        {/* Dispute Modal */}
        {showDisputeModal && (
          <div className="absolute inset-0 z-50 bg-black/85 backdrop-blur-sm p-6 flex flex-col justify-center animate-in fade-in">
            <div className="bg-slate-900 border border-rose-800/80 rounded-3xl p-6 space-y-4 max-w-md mx-auto w-full shadow-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-rose-950 border border-rose-800 text-rose-400 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-white">Report / Dispute Booking</h4>
                  <p className="text-[11px] text-slate-400 font-mono">Booking #{booking.id}</p>
                </div>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Filing a dispute immediately halts escrow payout release. Our admin moderation team
                will audit chat logs, check-in timestamps, and OTP records.
              </p>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">
                  Reason for dispute:
                </label>
                <textarea
                  rows={3}
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  placeholder="Describe the issue (e.g. companion late, location change refusal, inappropriate behavior)..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-rose-600"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDisputeModal(false)}
                  className="flex-1 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDisputeSubmit}
                  disabled={!disputeReason.trim() || isDisputing}
                  className="flex-1 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-40 text-white font-bold text-xs cursor-pointer"
                >
                  {isDisputing ? 'Submitting...' : 'Submit Dispute'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
